import { StateCreator } from 'zustand';
import { UserProfile, BrandKit, UserPreferences } from '@/types/User';
import { saveProfileToStorage, getProfileFromStorage } from '@/services/storage/repository';
import { Timestamp } from 'firebase/firestore';
import { logger } from '@/utils/logger';
import { auth } from '@/services/firebase';
import { isAnonymousOrDemoUser, isDemoUserId } from '@/utils/authGuards';

let globalProfileUnsubscribe: (() => void) | null = null;
let profileBoundaryGeneration = 0;

export function clearProfileSubscriptionForAccountBoundary(): void {
    profileBoundaryGeneration += 1;
    globalProfileUnsubscribe?.();
    globalProfileUnsubscribe = null;
}

export interface Organization {
    id: string;
    name: string;
    plan: 'free' | 'pro' | 'founder' | 'enterprise';
    members: string[];
    ownerId?: string;
    memberRoles?: Record<string, 'owner' | 'manager' | 'producer' | 'member'>;
    founderReadiness?: import('@/types/distribution').FounderReadiness;
}

export interface ProfileSlice {
    currentOrganizationId: string;
    organizations: Organization[];
    userProfile: UserProfile;
    setOrganization: (id: string) => void;
    addOrganization: (org: Organization) => void;
    setUserProfile: (profile: UserProfile) => void;
    updateBrandKit: (updates: Partial<BrandKit>) => Promise<void>;
    loadUserProfile: (uid: string) => Promise<void>;
    logout: () => Promise<void>;
    setTheme: (theme: 'dark' | 'light' | 'system') => void;
    updatePreferences: (updates: Partial<UserPreferences>) => void;
}

// Default Brand Kit — Intentionally empty so new users trigger onboarding
const DEFAULT_BRAND_KIT: BrandKit = {
    colors: [],
    fonts: '',
    brandDescription: '',
    negativePrompt: '',
    socials: {},
    brandAssets: [],
    referenceImages: [],
    releaseDetails: {
        title: '',
        type: '',
        artists: '',
        genre: '',
        mood: '',
        themes: '',
        lyrics: ''
    }
};

const DEFAULT_USER_PROFILE: UserProfile = {
    id: 'pending',
    uid: '',
    email: '',
    displayName: '',
    photoURL: null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastLoginAt: Timestamp.now(),
    emailVerified: false,
    membership: { tier: 'free', expiresAt: null },
    accountType: 'artist',

    bio: '',
    preferences: {
        theme: 'system',
        notifications: true,
        observabilityEnabled: false,
        showCognitiveLogicByDefault: false,
        agentAmbition: 'balanced',
        ideaAcceptanceCount: 0,
        lastAmbitionPromptTime: 0
    },
    brandKit: DEFAULT_BRAND_KIT,
    analyzedTrackIds: [],
    knowledgeBase: [],
    savedWorkflows: [],
    careerStage: '',
    goals: []
};

export const createProfileSlice: StateCreator<ProfileSlice> = (set, get) => ({
    currentOrganizationId: 'org-default',
    organizations: [],
    userProfile: DEFAULT_USER_PROFILE,
    // Auth state delegated to AuthSlice
    setOrganization: (id) => {
        set((state) => {
            const newProfile = { ...state.userProfile, currentOrganizationId: id };
            saveProfileToStorage(newProfile).catch(err => logger.error("[ProfileSlice] Failed to save org change:", err));
            return { currentOrganizationId: id, userProfile: newProfile };
        });
    },
    addOrganization: (org) => set((state) => ({ organizations: [...state.organizations, org] })),
    setUserProfile: (profile) => {
        const newProfile = { ...profile, updatedAt: Timestamp.now() };
        set({ userProfile: newProfile });
        // Persistence Strategy: Hybrid (IndexedDB for speed + Firestore for cloud backup)
        saveProfileToStorage(newProfile).catch(err => logger.error("[ProfileSlice] Failed to save profile:", err));

        if (newProfile.brandKit?.colors && newProfile.brandKit.colors.length > 0) {
            import('@/services/brand/BrandSyncService').then(({ brandSyncService }) => {
                const normalized = brandSyncService.normalizeColors(newProfile.brandKit!.colors);
                brandSyncService.applyCssVariables(normalized);
                brandSyncService.broadcastUpdate(normalized);
            }).catch(() => {});
        }
    },
    updateBrandKit: async (updates) => {
        const state = get();
        const currentBrandKit = state.userProfile.brandKit || DEFAULT_BRAND_KIT;
        const newProfile = {
            ...state.userProfile,
            brandKit: { ...currentBrandKit, ...updates },
            updatedAt: Timestamp.now()
        };
        set({ userProfile: newProfile });
        // Await persistence before returning so caller knows the write succeeded
        await saveProfileToStorage(newProfile);

        // Automated Visual DNA Cascading Protocol:
        // When brand colors change, cascade to DOM CSS variables, UI event bus,
        // and Tier 0 Artist Master Directive
        if (updates.colors && updates.colors.length > 0) {
            try {
                const { brandSyncService } = await import('@/services/brand/BrandSyncService');
                const normalized = brandSyncService.normalizeColors(updates.colors);
                brandSyncService.applyCssVariables(normalized);
                brandSyncService.broadcastUpdate(normalized);
                brandSyncService.syncToArtistDirective(
                    normalized,
                    updates.aestheticStyle || currentBrandKit.aestheticStyle,
                    'Store updateBrandKit synchronized brand palette'
                ).catch((err: unknown) => {
                    logger.warn('[ProfileSlice] Failed to cascade brand colors to Artist Directive:', err);
                });
            } catch (syncErr: unknown) {
                logger.warn('[ProfileSlice] BrandSyncService cascade failed:', syncErr);
            }
        }
    },
    loadUserProfile: async (uid: string) => {
        logger.info('[Profile] Loading user profile for:', uid);

        // Guard: If uid is the default 'pending' placeholder, skip Firestore calls
        if (!uid || uid === 'pending') {
            logger.debug('[Profile] Skipping load — no real UID yet.');
            return;
        }
        const loadGeneration = profileBoundaryGeneration;
        const isCurrentLoad = () => loadGeneration === profileBoundaryGeneration;

        // Guard: In E2E tests, if a user profile is already seeded with matching uid,
        // do not load/overwrite it to avoid race conditions with offline/mock fallback loading.
        const currentProfile = get().userProfile;
        const { isTestHarnessRuntime } = await import('@/utils/e2eMode');
        if (!isCurrentLoad()) return;
        if (currentProfile && currentProfile.uid === uid && currentProfile.displayName && isTestHarnessRuntime()) {
            logger.info('[Profile] Skipping loadUserProfile — profile already seeded in E2E runtime.');
            return;
        }

        const currentUser = auth.currentUser;
        const isCurrentAnonymousUser = currentUser?.uid === uid && isAnonymousOrDemoUser(currentUser);
        if (isDemoUserId(uid) || isCurrentAnonymousUser) {
            logger.error('[Profile] Refusing to load anonymous/demo profile UID in runtime.');
            return;
        }

        try {
            // Try to get from Firestore first (via Service/Repo) 
            const profile = await getProfileFromStorage(uid);
            if (!isCurrentLoad()) return;

            // --- Fetch Organizations (Fix for Permission Errors) ---
            try {
                const { db } = await import('@/services/firebase');
                const { collection, query, where, getDocs } = await import('firebase/firestore');

                const orgsRef = collection(db, 'organizations');
                const q = query(orgsRef, where('members', 'array-contains', uid));
                const orgSnap = await getDocs(q);
                if (!isCurrentLoad()) return;

                const userOrgs: Organization[] = [];
                orgSnap.forEach((doc) => {
                    const data = doc.data();
                    userOrgs.push({
                        id: doc.id,
                        name: data.name || 'Untitled Org',
                        plan: data.plan || 'free',
                        members: data.members || [],
                        ownerId: data.ownerId,
                        memberRoles: data.memberRoles
                    });
                });

                if (userOrgs.length > 0) {
                    logger.info('[Profile] Loaded organizations:', userOrgs.map(o => o.id));
                    set({ organizations: userOrgs });

                    // Resolve Current Org ID
                    // Resolve Current Org ID from Cloud Profile Preference
                    const preferredOrgId = profile?.currentOrganizationId;
                    const isValidPreferred = userOrgs.find(o => o.id === preferredOrgId);

                    if (isValidPreferred) {
                        set({ currentOrganizationId: isValidPreferred.id });
                    } else {
                        // Default to the first found org and sync back to cloud if possible
                        logger.info('[Profile] Defaulting to first org:', userOrgs[0]!.id);
                        set({ currentOrganizationId: userOrgs[0]!.id });
                        if (profile && !profile.currentOrganizationId) {
                            const updatedProfile = { ...profile, currentOrganizationId: userOrgs[0]!.id };
                            saveProfileToStorage(updatedProfile).catch(err => logger.error("[ProfileSlice] Failed to sync default org:", err));
                        }
                    }
                }
            } catch (orgErr: unknown) {
                // Suppress permission-denied — expected when Firestore rules restrict org access
                const code = (orgErr as { code?: string })?.code;
                if (code === 'permission-denied') {
                    logger.debug('[Profile] Organizations unavailable — insufficient permissions (expected in dev).');
                } else {
                    logger.error('[Profile] Failed to load organizations:', orgErr);
                }
            }
            // -----------------------------------------------------

            if (!isCurrentLoad()) return;

            if (profile) {
                logger.info('[Profile] Loaded profile for:', uid);
                // A profile is presentation state only. Founder and paid access
                // are resolved by server-owned entitlements before any spend.
                set({ userProfile: profile });

                if (profile.brandKit?.colors && profile.brandKit.colors.length > 0) {
                    import('@/services/brand/BrandSyncService').then(({ brandSyncService }) => {
                        const normalized = brandSyncService.normalizeColors(profile.brandKit!.colors);
                        brandSyncService.applyCssVariables(normalized);
                        brandSyncService.broadcastUpdate(normalized);
                    }).catch(() => {});
                }
            } else {
                logger.info('[Profile] No profile found, creating default for:', uid);
                // Create a new profile for this user
                const newProfile = { ...DEFAULT_USER_PROFILE, id: uid, uid: uid };
                set({ userProfile: newProfile });
                await saveProfileToStorage(newProfile);
                if (!isCurrentLoad()) return;
            }

            // Set up real-time listener for the user profile using SubscriptionManager
            try {
                const { db } = await import('@/services/firebase');
                const { doc, onSnapshot } = await import('firebase/firestore');
                if (!isCurrentLoad()) return;

                const userRef = doc(db, 'users', uid);
                
                if (globalProfileUnsubscribe) {
                    globalProfileUnsubscribe();
                    globalProfileUnsubscribe = null;
                }

                const unsubscribe = onSnapshot(userRef, (docSnap) => {
                    if (!isCurrentLoad()) return;
                    if (docSnap.exists()) {
                        const cloudProfile = docSnap.data() as UserProfile;
                        
                        const currentProfile = get().userProfile;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const getMs = (t: any) => {
                            if (!t) return 0;
                            if (typeof t.toMillis === 'function') return t.toMillis();
                            if (t.seconds) return t.seconds * 1000 + (t.nanoseconds || 0) / 1000000;
                            // Also handle ISO strings if somehow it was converted
                            if (typeof t === 'string') return new Date(t).getTime();
                            return 0;
                        };
                        
                        const currentMs = getMs(currentProfile?.updatedAt);
                        const cloudMs = getMs(cloudProfile?.updatedAt);

                        if (currentMs > cloudMs) {
                            logger.info('[Profile] Ignoring stale cloud update (local is newer)');
                            return;
                        }

                        set({ userProfile: cloudProfile });

                        if (cloudProfile.brandKit?.colors && cloudProfile.brandKit.colors.length > 0) {
                            import('@/services/brand/BrandSyncService').then(({ brandSyncService }) => {
                                const normalized = brandSyncService.normalizeColors(cloudProfile.brandKit!.colors);
                                brandSyncService.applyCssVariables(normalized);
                                brandSyncService.broadcastUpdate(normalized);
                            }).catch(() => {});
                        }
                    }
                }, (error) => {
                    if (isCurrentLoad()) logger.error('[Profile] Real-time listener error:', error);
                });

                globalProfileUnsubscribe = unsubscribe;
            } catch (err: unknown) {
                logger.error('[Profile] Failed to initialize real-time listener:', err);
            }
        } catch (err: unknown) {
            logger.error('[Profile] Failed to load profile:', err);
        }
    },
    logout: async () => {
        try {
            clearProfileSubscriptionForAccountBoundary();
            const { useStore } = await import('@/core/store');
            useStore.getState().clearAllSubscriptions();
        } catch (err: unknown) {
            logger.error('[Profile] Failed to clear subscriptions on logout', err);
        }
        logger.info('[System] Logout requested - resetting session state...');
        // In a no-auth world, "logout" might just reset preferences or switch to a guest profile.
        // For now, we just reload the page to clear transient state
        window.location.reload();
    },
    setTheme: (theme) => set((state) => {
        // Assume preferences is object now
        const preferences = state.userProfile.preferences || { notifications: true, theme: 'system' };

        const newProfile = {
            ...state.userProfile,
            preferences: { ...preferences, theme },
            updatedAt: Timestamp.now()
        };
        saveProfileToStorage(newProfile).catch(err => logger.error("[ProfileSlice] Failed to save theme update:", err));
        return { userProfile: newProfile };
    }),
    updatePreferences: (updates: Partial<UserPreferences>) => set((state) => {
        const currentPrefs = state.userProfile.preferences || { theme: 'system', notifications: true };
        const newProfile = {
            ...state.userProfile,
            preferences: { ...currentPrefs, ...updates },
            updatedAt: Timestamp.now()
        };
        saveProfileToStorage(newProfile).catch(err => logger.error("[ProfileSlice] Failed to save preferences:", err));
        return { userProfile: newProfile };
    })
});
